import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const DepartmentsContext = createContext(null);

/**
 * Slugs that exist as Department records but should NOT appear in the
 * navigation lists (dropdown, home grid, list page, footer, contact select).
 * They render as standalone specialised pages via dedicated routes.
 */
const HIDDEN_SLUGS = new Set();

export function DepartmentsProvider({ children }) {
  const [allDepartments, setAllDepartments] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/departments");
      setAllDepartments(Array.isArray(data) ? data : []);
    } catch {
      setAllDepartments([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Public/nav-facing list excludes hidden landing-page slugs.
  const departments = useMemo(
    () => allDepartments.filter((d) => !HIDDEN_SLUGS.has(d.slug)),
    [allDepartments]
  );

  const findBySlug = useCallback(
    (slug) => allDepartments.find((d) => d.slug === slug) || null,
    [allDepartments]
  );

  return (
    <DepartmentsContext.Provider value={{ departments, allDepartments, loaded, refresh, findBySlug }}>
      {children}
    </DepartmentsContext.Provider>
  );
}

export const useDepartments = () => useContext(DepartmentsContext);

